import React, {ReactNode} from 'react';
import {capitalize, isEmpty, pick, sum} from 'lodash';
import fp, {flow} from 'lodash/fp';
import {Link, withRouter, RouteComponentProps} from 'react-router-dom';

import InfoTooltip from 'shared/components/info-tooltip';

const TIER_GOALS: Record<string, number> = {safety: 2, target: 3, reach: 2};
const TIER_NAMES = Object.keys(TIER_GOALS);

interface AdmissionLikelihoodTierCounts {
  safety?: number;
  target?: number;
  reach?: number;
  [key: string]: number | undefined;
}

interface Props extends RouteComponentProps {
  totalCredits: number;
  admissionLikelihoodTierCounts: AdmissionLikelihoodTierCounts;
}

const BalancedListAdvice = ({
  totalCredits,
  admissionLikelihoodTierCounts,
  location,
  history,
}: Props) => {
  const determineAdvice = (): {adviceEmoji: string; adviceText: ReactNode} => {
    if (totalCredits < 10) {
      const difference = 10 - totalCredits;
      return {
        adviceEmoji: '🔒',
        adviceText: `Add ${difference} credit${
          difference === 1 ? '' : 's'
        } and fill out your portfolio to balance your college list.`,
      };
    }

    const tiers = pick(admissionLikelihoodTierCounts, TIER_NAMES);
    const totalFollowed = sum(Object.values(tiers));

    if (totalFollowed < 7) {
      return {
        adviceEmoji: '🔍',
        adviceText: (
          <span>
            When choosing where to apply, consider 7-12 colleges to create your list. &nbsp;
            <Link to="/colleges/recommendations">Discover colleges</Link>
          </span>
        ),
      };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mapValuesWithKey = (fp.mapValues as any).convert({cap: false});
    const tiersUnderGoals = flow(
      mapValuesWithKey((tierGoal: number, tier: string) => tierGoal - (tiers[tier] || 0)),
      fp.pickBy((tierDifference: number) => tierDifference > 0),
      fp.keys,
      fp.take(2)
    )(TIER_GOALS);

    if (tiersUnderGoals.length > 0) {
      return {
        adviceEmoji: '🔍',
        adviceText: (
          <span>
            {`Consider adding more ${tiersUnderGoals
              .map((string: string) => `${string.charAt(0).toUpperCase()}${string.slice(1)}`)
              .join(' and ')} colleges to your list.`}
            &nbsp;
            <Link to="/colleges/recommendations">Discover colleges</Link>
          </span>
        ),
      };
    }

    if (totalFollowed >= 20) {
      return {
        adviceEmoji: '💡',
        adviceText:
          'Consider applying to 7-12 colleges: 2-4 Safety, 3-5 Target, and 2-3 Reach colleges.',
      };
    }

    return {
      adviceEmoji: '🙌',
      adviceText:
        'Looking good! You have a well-balanced list of Safety, Target, and Reach colleges.',
    };
  };

  const renderScore = (propName: string) => {
    const scoresLoaded = !isEmpty(admissionLikelihoodTierCounts);

    let numberText: string | number = '0';
    if (totalCredits < 10) {
      numberText = '?';
    } else if (scoresLoaded) {
      numberText = admissionLikelihoodTierCounts[propName] || 0;
    }

    return (
      <div className="balanced-list-advice-value" key={propName}>
        <span className="tier-count" data-testid="tier-count">
          {numberText}
        </span>
        &nbsp;
        {scoresLoaded && capitalize(propName)}
      </div>
    );
  };

  const scoreComponents = TIER_NAMES.map(renderScore);
  const {adviceText, adviceEmoji} = determineAdvice();
  const {pathname} = location;
  const {push} = history;

  return (
    <div className="balanced-list-advice" data-testid="list-advice">
      <div className="balanced-list-advice-top">
        <h2 className="balanced-list-advice-title">
          Creating a balanced college list
          <InfoTooltip onClick={() => push(`${pathname}/byl-info`)}>Opens dialog</InfoTooltip>
        </h2>
        <div className="beta">BETA</div>
      </div>
      <div className="balanced-list-advice-content">
        <div className="admission-likelihood-score-counts">{scoreComponents}</div>
        <div className="balanced-list-advice-info">
          <div className="advice-icon">{adviceEmoji}</div>
          <p>{adviceText}</p>
        </div>
      </div>
    </div>
  );
};

export default withRouter(BalancedListAdvice);
